// Modal de creación de tareas de "Mis tareas".
// Reemplaza el formulario desplegable que se abría bajo la cabecera y lo
// comparten las tres pestañas: el botón "Nueva tarea", el "+ Agregar tarea"
// de cada columna del Tablero y el clic en un día del Calendario.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Check } from 'lucide-react';
import { FiCalendar, FiFolder, FiFlag, FiRepeat, FiLoader } from 'react-icons/fi';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';
// SUPABASE SWITCH: revert to '../services/firestore' for Firebase
import { createTask, getFoldersForProject, getListsForFolder } from '../services/firestore';
import DatePickerPopover from './DatePickerPopover';
import CustomSelect from '../../../components/ui/CustomSelect';

const RECURRENCE_OPTIONS = [
    { value: 'none', label: 'Sin repetición' },
    { value: 'daily', label: 'Diaria' },
    { value: 'weekdays', label: 'Días laborales (L-V)' },
    { value: 'weekly', label: 'Semanal' },
    { value: 'biweekly', label: 'Quincenal' },
    { value: 'monthly', label: 'Mensual' },
    { value: 'quarterly', label: 'Trimestral' },
];

const PRIORITIES = [
    { value: 'baja', label: 'Baja', dot: 'bg-gray-400', active: 'bg-gray-100 text-gray-700 border-gray-300' },
    { value: 'media', label: 'Media', dot: 'bg-amber-500', active: 'bg-amber-50 text-amber-700 border-amber-300' },
    { value: 'alta', label: 'Alta', dot: 'bg-red-500', active: 'bg-red-50 text-red-700 border-red-300' },
];

// Recuerda la última ubicación usada para no volver a elegir proyecto/carpeta/
// lista en cada tarea. Es una preferencia trivial: localStorage basta.
const LAST_LOCATION_KEY = 'myTasks_lastLocation';
const readLastLocation = () => {
    try { return JSON.parse(localStorage.getItem(LAST_LOCATION_KEY)) || null; } catch { return null; }
};

const TaskCreateModal = ({ open, onClose, user, projects, defaults = {}, onCreated }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [location, setLocation] = useState({ projectId: '', folderId: '', listId: '' });
    const [folders, setFolders] = useState([]);
    const [lists, setLists] = useState([]);
    const [loadingLocation, setLoadingLocation] = useState(false);
    const [date, setDate] = useState('');
    const [recurrence, setRecurrence] = useState('none');
    const [priority, setPriority] = useState('media');
    const [saving, setSaving] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [datePickerPos, setDatePickerPos] = useState(null);
    const inputRef = useRef(null);

    const loadLists = useCallback(async (projectId, folderId, preferredListId) => {
        setLists([]);
        if (!folderId || !user?.uid) return;
        setLoadingLocation(true);
        try {
            const found = await getListsForFolder(user.uid, projectId, folderId);
            setLists(found);
            const preferred = found.find(l => l.id === preferredListId);
            if (preferred) setLocation(prev => ({ ...prev, listId: preferred.id }));
            else if (found.length === 1) setLocation(prev => ({ ...prev, listId: found[0].id }));
        } catch { setLists([]); }
        setLoadingLocation(false);
    }, [user?.uid]);

    const loadFolders = useCallback(async (projectId, preferred = {}) => {
        setFolders([]);
        setLists([]);
        if (!projectId || !user?.uid) return;
        setLoadingLocation(true);
        try {
            const found = await getFoldersForProject(user.uid, projectId);
            setFolders(found);
            const target = found.find(f => f.id === preferred.folderId) || (found.length === 1 ? found[0] : null);
            if (target) {
                setLocation(prev => ({ ...prev, folderId: target.id, listId: '' }));
                await loadLists(projectId, target.id, preferred.listId);
            }
        } catch { setFolders([]); }
        setLoadingLocation(false);
    }, [user?.uid, loadLists]);

    const handleSelectProject = (projectId) => {
        setLocation({ projectId, folderId: '', listId: '' });
        loadFolders(projectId);
    };

    const handleSelectFolder = (folderId) => {
        setLocation(prev => ({ ...prev, folderId, listId: '' }));
        loadLists(location.projectId, folderId);
    };

    // Al abrir: estado limpio, valores heredados de la columna/día y la última
    // ubicación usada (o el primer proyecto si es la primera vez).
    useEffect(() => {
        if (!open) return;
        setTitle('');
        setDescription('');
        setDate(defaults.fechaVencimiento || '');
        setRecurrence(defaults.recurrence || 'none');
        setPriority(defaults.prioridad || 'media');
        setSaving(false);
        setShowDatePicker(false);

        const last = readLastLocation();
        const startProject = (last?.projectId && projects?.some(p => p.id === last.projectId))
            ? last.projectId
            : projects?.[0]?.id;
        if (startProject) {
            setLocation({ projectId: startProject, folderId: '', listId: '' });
            loadFolders(startProject, last?.projectId === startProject ? last : {});
        } else {
            setLocation({ projectId: '', folderId: '', listId: '' });
        }
        setTimeout(() => inputRef.current?.focus(), 60);
        // Sólo al abrir: no queremos reiniciar el formulario mientras se escribe.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Escape cierra el modal.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape' && !showDatePicker) onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose, showDatePicker]);

    const canSave = title.trim() && location.listId && !saving;

    const handleSubmit = async (keepOpen = false) => {
        if (!canSave || !user?.uid) return;
        setSaving(true);
        try {
            const taskData = {
                titulo: title.trim(),
                asignados: [user.email],
                prioridad: priority,
            };
            if (description.trim()) taskData.descripcion = description.trim();
            if (date) taskData.fechaVencimiento = date;
            if (recurrence !== 'none') taskData.recurrence = recurrence;

            const created = await createTask(user.uid, location.projectId, location.folderId, location.listId, taskData);
            localStorage.setItem(LAST_LOCATION_KEY, JSON.stringify(location));
            toast.success('Tarea creada');
            onCreated?.(created, taskData);

            if (keepOpen) {
                setTitle('');
                setDescription('');
                setSaving(false);
                inputRef.current?.focus();
            } else {
                onClose();
            }
        } catch (err) {
            console.error('Error creating task:', err);
            toast.error(err?.message || 'Error al crear la tarea');
            setSaving(false);
        }
    };

    if (!open) return null;

    const projectName = projects?.find(p => p.id === location.projectId);

    return createPortal(
        <div
            className="fixed inset-0 z-[70] flex items-start justify-center bg-slate-900/40 backdrop-blur-[2px] p-4 pt-[8vh] animate-in fade-in duration-150"
            onClick={onClose}
        >
            <div
                onClick={e => e.stopPropagation()}
                className="w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-gray-200/80 overflow-hidden animate-in zoom-in-95 slide-in-from-top-2 duration-150"
            >
                {/* Cabecera */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
                    <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center">
                            <Plus size={15} className="text-white" />
                        </div>
                        <h2 className="text-[15px] font-bold text-gray-900">Nueva tarea</h2>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                        <X size={17} />
                    </button>
                </div>

                {/* Cuerpo */}
                <div className="px-5 py-4 space-y-4">
                    <div>
                        <input
                            ref={inputRef}
                            type="text"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(false); } }}
                            placeholder="¿Qué hay que hacer?"
                            className="w-full text-[17px] font-medium text-gray-900 placeholder:text-gray-300 border-0 border-b-2 border-gray-100 focus:border-blue-500 pb-2 outline-none transition-colors"
                        />
                    </div>

                    <textarea
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder="Agrega una descripción (opcional)"
                        rows={2}
                        className="w-full text-[13px] text-gray-700 placeholder:text-gray-300 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 resize-none transition-all"
                    />

                    {/* Ubicación */}
                    <div>
                        <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-2">
                            <FiFolder size={11} /> Ubicación
                        </label>
                        <div className="flex items-center gap-2 flex-wrap">
                            <CustomSelect
                                size="sm"
                                value={location.projectId}
                                onChange={handleSelectProject}
                                options={(projects || []).map(p => ({ value: p.id, label: p.nombre || p.name }))}
                                placeholder="Proyecto..."
                            />
                            {folders.length > 0 && (
                                <CustomSelect
                                    size="sm"
                                    value={location.folderId}
                                    onChange={handleSelectFolder}
                                    options={folders.map(f => ({ value: f.id, label: f.nombre || f.name }))}
                                    placeholder="Carpeta..."
                                />
                            )}
                            {lists.length > 0 && (
                                <CustomSelect
                                    size="sm"
                                    value={location.listId}
                                    onChange={(v) => setLocation(prev => ({ ...prev, listId: v }))}
                                    options={lists.map(l => ({ value: l.id, label: l.nombre || l.name }))}
                                    placeholder="Lista..."
                                />
                            )}
                            {loadingLocation && <FiLoader size={13} className="text-blue-400 animate-spin" />}
                            {location.listId && !loadingLocation && (
                                <span className="flex items-center gap-1 text-[11px] text-green-600 font-semibold">
                                    <Check size={12} /> Listo
                                </span>
                            )}
                        </div>
                        {!location.listId && !loadingLocation && (
                            <p className="text-[11px] text-amber-600 mt-1.5">
                                {projectName ? 'Elige la carpeta y la lista donde va la tarea.' : 'Elige un proyecto.'}
                            </p>
                        )}
                    </div>

                    {/* Fecha · Repetición · Prioridad */}
                    <div className="flex items-center gap-2 flex-wrap pt-1">
                        <div className="relative">
                            <button
                                onClick={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setDatePickerPos({ top: rect.bottom + 4, left: rect.left });
                                    setShowDatePicker(v => !v);
                                }}
                                className={`flex items-center gap-1.5 text-[12px] px-3 py-1.5 border rounded-lg font-medium transition-colors ${date ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'}`}
                            >
                                <FiCalendar size={12} />
                                {date ? format(new Date(date), "d 'de' MMM", { locale: es }) : 'Fecha'}
                                {date && (
                                    <span onClick={(e) => { e.stopPropagation(); setDate(''); }} className="ml-0.5 text-blue-400 hover:text-red-500">
                                        <X size={11} />
                                    </span>
                                )}
                            </button>
                            {showDatePicker && datePickerPos && (
                                <DatePickerPopover
                                    date={date || null}
                                    onDateChange={(iso) => { setDate(iso || ''); setShowDatePicker(false); }}
                                    onClose={() => setShowDatePicker(false)}
                                    position={datePickerPos}
                                />
                            )}
                        </div>

                        <div className="flex items-center gap-1.5">
                            <FiRepeat size={12} className="text-gray-400" />
                            <CustomSelect
                                size="sm"
                                value={recurrence}
                                onChange={setRecurrence}
                                options={RECURRENCE_OPTIONS}
                                placeholder="Repetir"
                            />
                        </div>

                        <div className="flex items-center gap-1 ml-auto">
                            <FiFlag size={12} className="text-gray-400 mr-0.5" />
                            {PRIORITIES.map(p => (
                                <button
                                    key={p.value}
                                    onClick={() => setPriority(p.value)}
                                    className={`flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg border font-medium transition-all ${priority === p.value ? p.active : 'border-transparent text-gray-500 hover:bg-gray-100'}`}
                                >
                                    <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`} />
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Pie */}
                <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-t border-gray-100">
                    <span className="text-[11px] text-gray-400">
                        <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono">Enter</kbd> para crear
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handleSubmit(true)}
                            disabled={!canSave}
                            className="px-3 py-2 text-[12px] font-medium text-gray-600 hover:bg-gray-200 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            Crear y seguir
                        </button>
                        <button
                            onClick={() => handleSubmit(false)}
                            disabled={!canSave}
                            className="px-5 py-2 bg-blue-600 text-white text-[12.5px] font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                        >
                            {saving ? <FiLoader size={13} className="animate-spin" /> : <Plus size={14} />}
                            Crear tarea
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TaskCreateModal;
